// ── Per-tenant organization identity ──
// The lender's own name, as it appears in UI chrome: logo alt text, the browser
// tab, the sample URL in a settings placeholder, a sentence that says who keyed
// a number in. Sibling of tenantTheme.js (colors) and terms.js (vocabulary) —
// same shape, same lifecycle, applied in index.js before first paint.
//
// WHY THIS EXISTS
//   The name used to be typed into a dozen files. Every one of them was a place
//   a new licensee's deployment would still say someone else's name. Routing it
//   through one module means a tenant sets one env var and the whole surface
//   follows.
//
// WHAT DOES **NOT** BELONG HERE
//   - Generated borrower documents (invoices, interest statements). Those carry
//     the licensee's own branding and are configured separately.
//   - The certificate worksheet name bbcParser matches on. That is server
//     side (ORG_JSON bbcSheet in api/shared/org.js), because it is in files
//     borrowers send, not in anything this bundle draws.
//   - Database column names. Those are a schema migration, not a display string.
//
// Override per tenant with a build-time env var (single-tenant-per-deployment):
//   REACT_APP_ORG_JSON = {"name":"Meridian Credit","short":"Meridian"}
// (only the keys you want to change). Or call configureOrg(obj) with an
// API-sourced config if you'd rather do it at runtime.

const DEFAULT_ORG = {
  // Legal / display name of the lender operating this deployment.
  name:        "Aequum Capital",
  // Short form for tight spaces (tab titles, breadcrumbs).
  short:       "Aequum",
  // What the platform itself is called, independent of who runs it.
  productName: "Enterprise Platform",
  // Shown as the example in the client-portal URL field. Illustrative only —
  // it is a placeholder, not a default value.
  portalUrlExample: "https://client.example.com/",
  // The lender's own mail domain — used for example addresses in settings
  // placeholders, never as a value that is saved.
  emailDomain: "aequumcapital.com",
  // Box web address for links to generated files (invoices, statements).
  // "https://app.box.com" works for any Box tenant; an enterprise subdomain
  // just skips one redirect.
  boxBase:     "https://aequumcapital.app.box.com",
};

let ORG = { ...DEFAULT_ORG };

// Merge tenant overrides over the defaults. Safe to call with null/partial.
export function configureOrg(overrides) {
  ORG = { ...DEFAULT_ORG, ...(overrides || {}) };
}

// Read the build-time org config (per-tenant bundle). Returns null if unset/invalid.
export function loadOrgFromEnv() {
  try { return JSON.parse(process.env.REACT_APP_ORG_JSON || "null"); }
  catch (e) { return null; }
}

// Look up an org field. Falls back to the provided default, then the key itself.
export function org(key, fallback) {
  return (key in ORG) ? ORG[key] : (fallback !== undefined ? fallback : key);
}

// "Meridian Credit Enterprise Platform" — the string for a browser tab or a
// login header. Kept as a function so a tenant that overrides only `name` still
// gets a coherent title.
export function orgTitle() {
  const n = org("name"), p = org("productName");
  return p ? `${n} ${p}` : n;
}
